import React, { useState, useEffect } from 'react';
import Button from '../../ui/Button/Button';
import Icon from '../../ui/Icon';
import Dialog from '../../ui/Dialog/Dialog';
import Stat, { StatGrid } from '../../ui/Stat/Stat';
import { ICONS } from '../../ui/icon-map';
import { custodyService } from '../../services/custodyService';
import CustodyForm from './components/CustodyForm';
import CustodyList from './components/CustodyList';
import Layout from '../../components/Layout';
import RequirePermission from '../../components/RequirePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney } from '../../constants/finance';

const CustodiesDashboard = () => {
    const [stats, setStats] = useState({ totalOpen: 0, totalSpent: 0, totalRemaining: 0, openCount: 0, settledCount: 0 });
    const [custodies, setCustodies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [loadError, setLoadError] = useState('');

    // Fetch Data: one read of the list, the tiles are counted from it. A failed / denied
    // read shows an error instead of "AED 0" and an empty list (audit ADM-18).
    const fetchData = async () => {
        setLoading(true);
        try {
            const listData = await custodyService.getAllCustodies();
            setStats(await custodyService.getGlobalStats(listData));
            setCustodies(listData.filter(c => c.status !== 'archived'));
            setLoadError('');
        } catch (error) {
            setCustodies([]);
            setLoadError(error?.code === 'permission-denied'
                ? 'Your role cannot read the petty cash records (database permission). Ask an admin.'
                : `The custodies could not be loaded (${error?.code || error?.message || 'unknown error'}).`);
        } finally {
            setLoading(false);
        }
    };
    const money = (v) => (loadError ? '-' : formatMoney(v));   // the one money format (DUP-27)

    useEffect(() => {
        fetchData();
    }, []);

    const handleCreateSuccess = () => {
        setShowCreateModal(false);
        fetchData(); // Refresh
    };

    return (
        <Layout
            title="Petty Cash Management"
            subtitle="Custodies, their expenses and settlements"
            actions={(
                <RequirePermission permission={PERMISSIONS.add_custody}>
                    <Button variant="primary" onClick={() => setShowCreateModal(true)} leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                        Open New Custody
                    </Button>
                </RequirePermission>
            )}
        >
            <div className="flex flex-col gap-4">

                {/* KPI tiles */}
                {loadError && (
                    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        <span className="flex-1 min-w-0">{loadError}</span>
                        <Button size="sm" variant="secondary" onClick={fetchData} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Retry</Button>
                    </div>
                )}

                <StatGrid min={200}>
                    <Stat label="Total Open Amount" value={money(stats.totalOpen)} icon={ICONS.wallet} loading={loading} />
                    <Stat label="Total Spent" value={money(stats.totalSpent)} icon={ICONS.operations} tone={loadError ? undefined : 'danger'} loading={loading} />
                    <Stat label="Total Remaining" value={money(stats.totalRemaining)} icon={ICONS.history} tone={loadError ? undefined : (stats.totalRemaining < 0 ? 'danger' : 'success')} loading={loading} />
                    <Stat label="Open Custodies" value={loadError ? '-' : stats.openCount} meta={loadError ? 'Could not load' : `+${stats.settledCount} Settled`} icon={ICONS.contracts} loading={loading} />
                </StatGrid>

                <h2 className="text-base font-semibold text-gray-900">Custodies list</h2>

                {!loadError && <CustodyList custodies={custodies} loading={loading} onRefresh={fetchData} />}

                {/* Create Modal */}
                <Dialog
                    open={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    title="Open New Custody"
                    closeOnOverlay={false}
                >
                    <CustodyForm onSuccess={handleCreateSuccess} onCancel={() => setShowCreateModal(false)} />
                </Dialog>
            </div>
        </Layout>
    );
};

export default CustodiesDashboard;
